import { useState } from 'react';
import type { CustomerImportAction, CustomerImportPreview, CustomerImportResult } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney } from '../format';
import { ACTION_LABEL, allowedActions, defaultAction } from '../customerImport';
import { useConfirm } from '../components/ConfirmDialog';

interface Props {
  onBack: () => void;
}

// Müştəri və təchizatçı idxalı: fayl → baxış (xətalar, bazada uyğunluq) → seçim → yazma; ilkin qalıq KÖÇ sənədi ilə.
export function CustomerImport({ onBack }: Props) {
  const confirm = useConfirm();
  const [preview, setPreview] = useState<CustomerImportPreview | null>(null);
  const [actions, setActions] = useState<CustomerImportAction[]>([]);
  const [result, setResult] = useState<CustomerImportResult | null>(null);
  const [reversed, setReversed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  const onFile = (file: File | undefined) => {
    if (!file) return;
    setResult(null);
    setReversed(false);
    run(async () => {
      const p = await api.customerImportPreview(file);
      setPreview(p);
      setActions(p.rows.map((r) => defaultAction(r)));
    });
  };

  const commit = () => {
    if (!preview) return;
    run(async () => {
      const r = await api.customerImportCommit({
        fileName: preview.fileName,
        rows: preview.rows.map((row, i) => ({
          row: row.row,
          action: actions[i],
          kind: row.kind,
          name: row.name,
          phone: row.phone,
          voen: row.voen,
          address: row.address,
          creditLimit: row.creditLimit,
          openingBalance: row.openingBalance,
          note: row.note,
        })),
      });
      setResult(r);
      setPreview(null);
    });
  };

  const reverse = async () => {
    if (!result?.docId || !(await confirm('İdxalın ilkin qalıqları geri alınsın? Müştərilər qalır, borclar əks sənədlə ləğv olunur.', { confirmLabel: 'Geri al', danger: true }))) return;
    run(async () => {
      await api.customerImportReverse(result.docId!);
      setReversed(true);
    });
  };

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn" onClick={onBack}>
          ← Müştərilər
        </button>
        <span className="brand">Müştəri idxalı</span>
      </header>
      <main className="page">
        <section className="card">
          <p className="muted small">
            Nümunə fayl: <span className="mono">docs/numune/Musteri_yukleme_numunesi.xlsx</span>. Sütunlar: növ, ad, telefon, VÖEN, ünvan, nisyə limiti,
            ilkin borc, qeyd. İlkin borc kassaya və növbəyə düşmür.
          </p>
          <input type="file" accept=".xlsx" aria-label="Excel faylı" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} />
        </section>

        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}

        {result && (
          <section className="card" aria-live="polite">
            <h2>İdxal yazıldı{result.displayNo ? ` — ${result.displayNo}` : ''}</h2>
            <p>
              Yaradıldı {result.created} · yeniləndi {result.updated} · keçildi {result.skipped} · ilkin qalıq cəmi {formatMoney(result.balanceTotal)}
            </p>
            {result.docId && !reversed && (
              <button type="button" className="btn-small" onClick={reverse} disabled={busy}>
                İdxalı geri al
              </button>
            )}
            {reversed && <p className="ok">İlkin qalıqlar geri alındı (LƏĞ sənədi).</p>}
          </section>
        )}

        {preview && (
          <section className="card">
            <h2>{preview.fileName}</h2>
            <p className="muted small">
              Düzgün {preview.valid} · xətalı {preview.invalid} · bazada var {preview.matched}
            </p>
            <table className="receipt-table">
              <thead>
                <tr>
                  <th>Sətir</th>
                  <th>Növ</th>
                  <th>Ad</th>
                  <th>Telefon</th>
                  <th>VÖEN</th>
                  <th>İlkin borc</th>
                  <th>Vəziyyət</th>
                  <th>Seçim</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={r.row}>
                    <td>{r.row}</td>
                    <td>{r.kind === 'supplier' ? 'təchizatçı' : 'müştəri'}</td>
                    <td>{r.name}</td>
                    <td>{r.phone ?? '—'}</td>
                    <td>{r.voen ?? '—'}</td>
                    <td>{formatMoney(r.openingBalance)}</td>
                    <td className={r.errors.length ? 'bad' : ''}>{r.errors.length ? r.errors.join('; ') : r.match ? `bazada: ${r.match.name}` : 'yeni'}</td>
                    <td>
                      <select
                        aria-label={`Sətir ${r.row} seçimi`}
                        value={actions[i]}
                        onChange={(e) => setActions((cur) => cur.map((a, k) => (k === i ? (e.target.value as CustomerImportAction) : a)))}
                      >
                        {allowedActions(r).map((a) => (
                          <option key={a} value={a}>
                            {ACTION_LABEL[a]}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn-primary" onClick={commit} disabled={busy || actions.every((a) => a === 'skip')}>
              Yaz
            </button>
          </section>
        )}
      </main>
    </div>
  );
}
